import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import PageBackground from "components/common/ArtistBackground";
import Button, { ButtonLink } from "components/common/Button";
import Menu from "components/Header/Menu";
import { queryInstanceArtist } from "queries/settings";
import { useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaHandHoldingHeart, FaUserAlt } from "react-icons/fa";
import { useLocation, useParams } from "react-router-dom";
import { useAuthContext } from "state/AuthContext";
import { getArtistUrl } from "utils/artist";
import usePublicArtist from "utils/usePublicObjectById";
import useShow from "utils/useShow";

import { bp } from "../../constants";

import HeaderLogo from "./HeaderLogo";
import HeaderSearch from "./HeaderSearch";

const HeaderWrapper = styled.header<{
  hasBackground?: boolean;
  transparent?: boolean;
  artistId?: boolean;
  show?: string;
  trackGroupId?: boolean;
  postId?: boolean;
  stripTint?: string;
}>`
  position: sticky;
  width: 100%;
  z-index: 999;
  transition: top 0.4s ease-out;

  ${(props) =>
    props.show === "up"
      ? `top: 0;
           transition: top 0.4s ease-out;`
      : ""}
  ${(props) =>
    props.show === "down"
      ? `top: -60px;
           transition: top 0.4s ease-out;`
      : ""}

  ${(props) =>
    props.transparent && !props.trackGroupId && !props.postId
      ? `background: transparent;
         box-shadow: 0px 1px 10px rgba(0, 0, 0, 0);`
      : `background-color: var(--mi-background-color);
         background-image: linear-gradient(${props.stripTint}, ${props.stripTint});
         box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.1);`}

  @media screen and (max-width: ${bp.medium}px) {
    position: sticky;
    display: flex;
    align-items: flex-start;

    ${(props) =>
      props.hasBackground && !props.trackGroupId && !props.postId
        ? `top: calc(var(--header-cover-sticky-height) - 25vw);
           aspect-ratio: 4 / 1;
           width: auto;
           min-height: auto;
           background: transparent;
           box-shadow: 0px 1px 10px rgba(0, 0, 0, 0);
           transition: top 0.4s ease-out;`
        : ""}
    ${(props) =>
      props.hasBackground &&
      !props.trackGroupId &&
      !props.postId &&
      props.show === "up"
        ? `top: calc(var(--header-cover-sticky-height) - 25vw);
           aspect-ratio: 4 / 1;
           width: auto;
           transition: top 0.4s ease-out;`
        : ""}
    ${(props) =>
      props.hasBackground &&
      !props.trackGroupId &&
      !props.postId &&
      props.show === "down"
        ? `top: calc(var(--header-cover-sticky-height) - 50vw);
           aspect-ratio: 4 / 1;
           width: auto;
           min-height: auto;
           transition: top 0.4s ease-out;`
        : ""}


    ${(props) => (props.trackGroupId || props.postId ? "aspect-ratio: 0;" : "")}
  }
`;

const Content = styled.div<{ artistId?: string }>`
  ${(props) =>
    props.artistId
      ? "max-width: 100%; transition: all ease-in-out .4s;"
      : "max-width: 1500px; transition: all ease-in-out .4s;"}
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  width: 100%;
  z-index: 999;
  position: sticky;
  top: 0px;
  margin: 0 auto;
  height: var(--header-cover-sticky-height);

  @media screen and (max-width: ${bp.medium}px) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding: var(--mi-side-paddings-xsmall);
    position: sticky;
    top: 0;
  }
`;

const menuButtonOverride = css`
  color: var(--mi-contrast-color) !important;
  svg {
    fill: var(--mi-contrast-color) !important;
  }
  &:hover:not(:disabled) {
    background-color: var(--mi-tint-color) !important;
    color: var(--mi-contrast-color) !important;
    svg {
      fill: var(--mi-contrast-color) !important;
    }
  }
`;

const Header = () => {
  const { t } = useTranslation();

  const { user } = useAuthContext();
  const isLoggedIn = !!user?.id;

  const { artistId, trackGroupId, postId } = useParams();
  const location = useLocation();

  const { object: artist } = usePublicArtist<Artist>("artists", artistId);
  const artistBackground = artist?.background?.sizes;

  const show = useShow();
  const transparent =
    !!artistBackground && !!artistId && !artist?.announcementText;

  const stripTint = artistId ? "var(--mi-button-tint-color)" : "transparent";

  const { data: instanceArtist } = useQuery(queryInstanceArtist());

  const menuRef = useRef<HTMLDialogElement | null>(null);
  const menuDialogId = "menu-dialog";

  const openMenu = () => {
    if (menuRef.current) {
      menuRef.current.showModal();
    }
  };

  const closeMenu = () => {
    if (menuRef.current) {
      menuRef.current.close();
    }
  };

  return (
    <HeaderWrapper
      transparent={transparent}
      hasBackground={!!artistBackground}
      show={show}
      trackGroupId={!!trackGroupId}
      postId={!!postId}
      artistId={!!artistId}
      stripTint={stripTint}
    >
      <div className="md:hidden!">
        <PageBackground />
      </div>
      <div className="absolute w-full h-full md:hidden!"></div>
      <Content artistId={artistId}>
        <HeaderLogo />
        <div className="flex items-center gap-3">
          {instanceArtist && !artistId && (
            <ButtonLink
              to={getArtistUrl(instanceArtist) + "/support"}
              collapsible
              startIcon={<FaHandHoldingHeart />}
              className="block no-underline text-center hover:underline! text-(--mi-background-color)! [&_svg]:fill-(--mi-background-color)! bg-(--mi-text-color)! max-md:text-(--mi-font-size-xsmall)!"
            >
              {t("donateNow", { keyPrefix: "kickstarter" })}
            </ButtonLink>
          )}
          <HeaderSearch />
          {!isLoggedIn && (
            <ButtonLink
              aria-label={t("logIn", { keyPrefix: "logIn" })}
              title={t("logIn", { keyPrefix: "logIn" })}
              startIcon={<FaUserAlt />}
              to={`/login?redirect=${encodeURIComponent(
                location.pathname + location.search
              )}`}
              variant="transparent"
              className={menuButtonOverride}
            />
          )}
          {isLoggedIn && (
            <Button
              aria-controls={menuDialogId}
              aria-haspopup="dialog"
              aria-label={t("menu", { keyPrefix: "headerMenu" })}
              data-cy="user-nav"
              // @ts-ignore React doesn't support Invoker Commands API
              command="show-modal"
              commandfor={menuDialogId}
              onClick={() => openMenu()}
              startIcon={
                <svg
                  width="16"
                  height="14"
                  viewBox="0 0 16 14"
                  fill="currentColor"
                  aria-hidden
                >
                  <rect y="0" width="16" height="3" rx="1.5" />
                  <rect y="5" width="16" height="3" rx="1.5" />
                  <rect y="10" width="16" height="3" rx="1.5" />
                </svg>
              }
              variant="transparent"
              className={menuButtonOverride}
              title={t("menu", { keyPrefix: "headerMenu" })}
            />
          )}
          {isLoggedIn &&
            createPortal(
              <Menu
                dialogId={menuDialogId}
                isAdmin={user.isAdmin}
                isLabelAccount={user.isLabelAccount}
                onClose={() => closeMenu()}
                ref={menuRef}
              />,
              document.body
            )}
        </div>
      </Content>
    </HeaderWrapper>
  );
};

export default Header;
